import { Button, DatePicker, Divider, Form, Input, Select, Tag, message, Modal } from 'antd';
import axios from 'axios';
import { LoadingSpinnerComponent } from 'components';
import CustomSelector, { CustomSelectorTabInterface } from 'components/selector/Selector';
import {
  AGE_RATING,
  CONTENT_TYPE,
  DUBBING_MODE,
  LANGUAGE_OPTIONS,
  PRODUCE_COUNTRIES,
  STATUS,
} from 'constants/index';
import { libraryStore } from 'content/library/stores/libraryStore';
import { LibraryInterface } from 'content/library/types/contentType';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { categoryApiService } from 'service/api/categoryApiService';
import { genreApiService } from 'service/api/genreApiService';
import { peopleApiService } from 'service/api/peopleApiService';
import { tagApiService } from 'service/api/tagApiService';
import { accountStore } from 'store/accountStore';
import { CategoryInterface } from 'type/categoryType';
import { GenreInterface } from 'type/genreType';
import { PeopleInterface } from 'type/peopleType';
import { TagInterface } from 'type/tagType';
import { EnhancedCastEditor } from '../../../../../shared/components/enhanced-cast';
import { I18nFormFields } from '../../../../../shared/components/i18n/I18nFormFields';
import { I18nService } from '../../../../../shared/services/i18n/i18nService';
import {
  ContentI18nData,
  I18nFieldData,
  I18nFormData,
} from '../../../../../shared/types/i18n/i18nTypes';
import { DISTRIBUTION_OPTIONS } from '../../constants';
import { libraryService } from '../../service/libraryService';

import './Detail.css';

dayjs.extend(utc);

const LibraryDetail: React.FC = () => {
  const url = `${window.location.origin}`;
  const [library, setLibrary] = React.useState<LibraryInterface | null>(null);
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm();
  const [peopleList, setPeopleList] = React.useState<PeopleInterface[]>([]);
  const [selectedPeoples, setSelectedPeoples] = React.useState<PeopleInterface[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const currentContentType = Form.useWatch('content_type', form);
  const user = accountStore.getAccount();
  const isPlatformOne = user?.platform.id === 1;
  const [genreList, setGenreList] = React.useState<GenreInterface[]>([]);
  const [tagList, setTagList] = React.useState<TagInterface[]>([]);
  const [categoryList, setCategoryList] = React.useState<CategoryInterface[]>([]);

  // Enhanced Cast 상태 관리
  const [enhancedCast, setEnhancedCast] = useState<{ cast: number; type: string }[]>([]);
  const [searchedPeopleList, setSearchedPeopleList] = useState<PeopleInterface[]>([]);

  // i18n data state management
  const [i18nData, setI18nData] = useState<ContentI18nData>(I18nService.createEmptyI18nData());
  const [formData, setFormData] = useState<I18nFormData>({
    title: '',
    title_en: '',
    synopsis: '',
    synopsis_en: '',
    i18n: I18nService.createEmptyI18nData(),
  });

  const genreTab: CustomSelectorTabInterface<GenreInterface> = {
    label: 'Genre',
    selected: genreList,
    setSelected: setGenreList,
    fetchSearch: (search: string) =>
      genreApiService
        .fetch(new URLSearchParams({ search, page: '1', page_size: '100' }))
        .then(res => res.results),
    getLabel: (genre: GenreInterface) => genre.slug,
  };

  const tagTab: CustomSelectorTabInterface<TagInterface> = {
    label: 'Tag',
    selected: tagList,
    setSelected: setTagList,
    fetchSearch: (search: string) =>
      tagApiService
        .fetch(new URLSearchParams({ search, page: '1', page_size: '100' }))
        .then(res => res.results),
    getLabel: (tag: TagInterface) => tag.name,
  };

  const tabs: CustomSelectorTabInterface<any>[] = [genreTab, tagTab];

  useEffect(() => {
    libraryService.fetchLibrary(Number(id)).then(library => {
      // Initialize i18n data
      const normalizedData = I18nService.safeNormalizeApiResponse(library);
      setFormData(normalizedData);
      setI18nData(normalizedData.i18n || I18nService.createEmptyI18nData());

      // Enhanced Cast 데이터 초기화
      if (library.enhanced_cast) {
        const enhancedCastData = library.enhanced_cast.map((item: any) => ({
          cast: item.cast,
          type: item.type,
        }));
        setEnhancedCast(enhancedCastData);
      }

      // Enhanced Cast의 cast ID들 추출
      const enhancedCastIds = library.enhanced_cast?.map((item: any) => item.cast) || [];

      form.setFieldsValue({
        ...library,
        distributions: library.distributions?.map(d => d.content_consumer_name) || [],
        release_date_start: library.release_date_start ? dayjs(library.release_date_start) : null,
        release_date_end: library.release_date_end ? dayjs(library.release_date_end) : null,
        // Set legacy fields with i18n data
        title: normalizedData.title,
        title_en: normalizedData.title_en,
        synopsis: normalizedData.synopsis,
        synopsis_en: normalizedData.synopsis_en,
        seo_title: library.seo_title || '',
        seo_synopsis: library.seo_synopsis || '',
        seo_keyword: library.seo_keyword || '',
        on_air_date: library.on_air_date ? dayjs(library.on_air_date) : null,
      });
      setLibrary(library);

      Promise.all([
        peopleApiService.fetch(new URLSearchParams({ page: '1', page_size: '100' })),
        peopleApiService.fetchByIds(library?.cast || [], { page: '1', page_size: '100' }),
        categoryApiService.fetch(new URLSearchParams({ page: '1', page_size: '100' })),
        genreApiService.fetchByIds(library?.genres || [], { page: '1', page_size: '100' }),
        tagApiService.fetchByIds(library?.tags || [], { page: '1', page_size: '100' }),
        // Enhanced Cast의 people도 가져오기
        enhancedCastIds.length > 0
          ? peopleApiService.fetchByIds(enhancedCastIds, { page: '1', page_size: '100' })
          : Promise.resolve({ results: [] }),
      ]).then(
        ([
          peopleResponse,
          peopleResponseByIds,
          categoryResponse,
          genreResponse,
          tagResponse,
          enhancedCastPeople,
        ]) => {
          setPeopleList(peopleResponse.results);
          setSelectedPeoples(peopleResponseByIds.results);

          // Enhanced Cast용 people 리스트 - 기본 100명 + enhanced cast people 합치기
          const allPeople = [...peopleResponse.results];
          if (enhancedCastPeople.results) {
            enhancedCastPeople.results.forEach((person: PeopleInterface) => {
              if (!allPeople.some(p => p.id === person.id)) {
                allPeople.push(person);
              }
            });
          }
          setSearchedPeopleList(allPeople);

          setCategoryList(categoryResponse.results);
          setGenreList(genreResponse.results);
          setTagList(tagResponse.results);

          // Set category in form after category list is loaded
          if (library.categories && library.categories.length > 0) {
            form.setFieldsValue({ category: library.categories[0] });
          }
        }
      );

      setIsLoading(false);
    });
  }, [id, form]);

  // Enhanced Cast를 위한 people 검색 핸들러
  const searchPeopleForEnhancedCast = async (value: string): Promise<void> => {
    if (!value.trim()) return;

    const response = await peopleApiService.fetch(
      new URLSearchParams({
        page: '1',
        page_size: '20',
        search: value,
      })
    );
    setSearchedPeopleList(response.results);
  };

  // i18n field change handler
  const handleI18nFieldChange = useCallback(
    (
      fieldName: 'title' | 'synopsis' | 'seo_title' | 'seo_synopsis' | 'seo_keyword',
      newValue: I18nFieldData
    ) => {
      const updatedI18nData = {
        ...i18nData,
        [fieldName]: newValue,
      };
      setI18nData(updatedI18nData);

      // Sync with legacy fields - simple mapping
      const legacyData = {
        title: updatedI18nData.title?.ko || formData.title || '',
        title_en: updatedI18nData.title?.en || formData.title_en,
        synopsis: updatedI18nData.synopsis?.ko || formData.synopsis,
        synopsis_en: updatedI18nData.synopsis?.en || formData.synopsis_en,
        seo_title: updatedI18nData.seo_title?.ko || '',
        seo_synopsis: updatedI18nData.seo_synopsis?.ko || '',
        seo_keyword: updatedI18nData.seo_keyword?.ko || '',
      };
      const updatedFormData = {
        ...formData,
        title: legacyData.title || '',
        title_en: legacyData.title_en,
        synopsis: legacyData.synopsis,
        synopsis_en: legacyData.synopsis_en,
        seo_title: legacyData.seo_title,
        seo_synopsis: legacyData.seo_synopsis,
        seo_keyword: legacyData.seo_keyword,
        i18n: updatedI18nData,
      };
      setFormData(updatedFormData);

      // Update Ant Design Form values
      form.setFieldsValue({
        title: updatedFormData.title,
        title_en: updatedFormData.title_en,
        synopsis: updatedFormData.synopsis,
        synopsis_en: updatedFormData.synopsis_en,
        seo_title: updatedFormData.seo_title,
        seo_synopsis: updatedFormData.seo_synopsis,
        seo_keyword: updatedFormData.seo_keyword,
      });
    },
    [i18nData, formData, form]
  );

  // Legacy field change handler (unused but kept for potential future use)

  const updateDuration = async (): Promise<void> => {
    const library = libraryStore.getLibrary(Number(id));
    if (library) {
      await libraryService.updateDuration(library.id);
      libraryStore.removeLibrary(library.id);
      const newLibrary = await libraryService.fetchLibrary(library.id);
      libraryStore.setLibrary(library.id, newLibrary);
    }
    message.success('Duration updated');
  };

  return (
    <>
      {isLoading ? (
        <LoadingSpinnerComponent />
      ) : (
        <div>
          <div className="content-details-page" style={{ display: 'flex' }}>
            <div className="content-details-edit">
              <Form form={form} layout="vertical" className="content-details-form">
                <I18nFormFields
                  fieldName="title"
                  required={true}
                  fieldType="input"
                  value={i18nData.title}
                  onChange={value => handleI18nFieldChange('title', value)}
                />
                <Form.Item label="Category" name="category">
                  <Select>
                    {categoryList.map(category => (
                      <Select.Option key={category.id} value={category.id}>
                        {category.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item>
                  <Form.Item name="genres" hidden />
                  <Form.Item name="tags" hidden />
                  <CustomSelector tabs={tabs} />
                </Form.Item>
                <Form.Item
                  label="Slug"
                  name="slug"
                  rules={[{ required: true, message: 'Please enter a slug' }]}
                >
                  <Input />
                </Form.Item>
                <Form.Item label="Content Type" name="content_type">
                  <Select>
                    {CONTENT_TYPE.map(contentType => (
                      <Select.Option key={contentType} value={contentType}>
                        {contentType}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item label="Status" name="status">
                  <Select>
                    {STATUS.map(status => (
                      <Select.Option key={status} value={status}>
                        {status}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item label="Dubbing Mode" name="dubbing_mode">
                  <Select>
                    {Object.entries(DUBBING_MODE).map(([key, value]) => (
                      <Select.Option key={key} value={key}>
                        {value}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                {/* Enhanced Cast Editor */}
                <Form.Item label="Enhanced Cast">
                  <EnhancedCastEditor
                    value={enhancedCast}
                    onChange={setEnhancedCast}
                    peopleList={searchedPeopleList}
                    onSearchPeople={searchPeopleForEnhancedCast}
                  />
                </Form.Item>

                <Form layout="inline">
                  <Form.Item label="Duration" name="duration">
                    <div className="form-value">{library?.duration || null}</div>
                  </Form.Item>
                  <Button type="link" onClick={() => updateDuration()}>
                    Reload
                  </Button>
                </Form>

                <I18nFormFields
                  fieldName="synopsis"
                  required={false}
                  fieldType="textarea"
                  value={i18nData.synopsis}
                  onChange={value => handleI18nFieldChange('synopsis', value)}
                />

                {currentContentType !== 'movie' && (
                  <>
                    <Form.Item
                      label="Episode Order"
                      name="episode_order"
                      rules={[{ required: true, message: 'Please Enter an Episode Order' }]}
                    >
                      <Input type="number" min={0} />
                    </Form.Item>
                    <Form.Item label="Episode Part" name="episode_part">
                      <Input type="number" min={0} />
                    </Form.Item>
                  </>
                )}
                {currentContentType !== 'movie' && (
                  <Form layout="inline">
                    <Form.Item
                      name="season"
                      style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}
                    >
                      <span style={{ marginRight: 8, cursor: 'pointer' }}>Season</span>
                      {library?.season?.id ? (
                        <Tag
                          color="blue"
                          className="season-tag"
                          onClick={() =>
                            window.open(
                              `${url}/season/edit/${library?.season?.id}`,
                              '_blank',
                              'noopener,noreferrer'
                            )
                          }
                        >
                          {library?.season?.title}
                        </Tag>
                      ) : (
                        <Button
                          type="link"
                          onClick={() => window.open('/season', '_blank', 'noopener,noreferrer')}
                        >
                          Edit
                        </Button>
                      )}
                    </Form.Item>
                  </Form>
                )}
                {currentContentType !== 'movie' && (
                  <Form layout="inline">
                    <Form.Item
                      name="series"
                      style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}
                    >
                      <span style={{ marginRight: 8, cursor: 'pointer' }}>Series</span>
                      {library?.series?.id ? (
                        <Tag
                          color="blue"
                          className="season-tag"
                          onClick={() =>
                            window.open(
                              `${url}/series/edit/${library?.series?.id}`,
                              '_blank',
                              'noopener,noreferrer'
                            )
                          }
                        >
                          {library?.series?.title}
                        </Tag>
                      ) : (
                        <Button
                          type="link"
                          onClick={() =>
                            window.open(`${url}/series`, '_blank', 'noopener,noreferrer')
                          }
                        >
                          Edit
                        </Button>
                      )}
                    </Form.Item>
                  </Form>
                )}
                <Form.Item label="Age Rating" name="age_rating">
                  <Select>
                    {AGE_RATING.map(ageRating => (
                      <Select.Option key={ageRating} value={ageRating}>
                        {ageRating}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item label="Produce Country" name="produce_country">
                  <Select
                    value={library?.produce_country}
                    style={{ width: 170 }}
                    options={PRODUCE_COUNTRIES}
                  ></Select>
                </Form.Item>
                <Form.Item label="Language" name="language">
                  <Select
                    value={library?.language}
                    style={{ width: 170 }}
                    options={LANGUAGE_OPTIONS}
                  ></Select>
                </Form.Item>
                <Form.Item label="Release Date Start" name="release_date_start">
                  <DatePicker />
                </Form.Item>
                <Form.Item label="Release Date End" name="release_date_end">
                  <DatePicker />
                </Form.Item>
                <Form.Item label="On Air Date" name="on_air_date">
                  <DatePicker />
                </Form.Item>
                <Form.Item name="score" label="Score">
                  <Input type="number" min={0} />
                </Form.Item>
                {isPlatformOne && (
                  <Form.Item label="Distributions" name="distributions">
                    <Select mode="multiple" placeholder="Select distributions">
                      {DISTRIBUTION_OPTIONS.map(option => (
                        <Select.Option key={option} value={option}>
                          {option}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}
                <Form.Item label="CP From ODX" name="cp_from_odx">
                  <Input />
                </Form.Item>
                <Divider orientation="left">SEO Multilingual Input</Divider>

                {/* SEO Title Multilingual Input Field */}
                <I18nFormFields
                  fieldName="seo_title"
                  required={false}
                  fieldType="input"
                  value={i18nData.seo_title}
                  onChange={value => handleI18nFieldChange('seo_title', value)}
                />

                {/* SEO Description Multilingual Input Field */}
                <I18nFormFields
                  fieldName="seo_synopsis"
                  required={false}
                  fieldType="textarea"
                  value={i18nData.seo_synopsis}
                  onChange={value => handleI18nFieldChange('seo_synopsis', value)}
                />

                {/* SEO Keyword Multilingual Input Field */}
                <I18nFormFields
                  fieldName="seo_keyword"
                  required={false}
                  fieldType="input"
                  value={i18nData.seo_keyword}
                  onChange={value => handleI18nFieldChange('seo_keyword', value)}
                />

                {/* <Form.Item label="SEO Title" name="seo_title">
                  <Input onChange={e => handleLegacyFieldChange('seo_title', e.target.value)} />
                </Form.Item>
                <Form.Item label="SEO Synopsis" name="seo_synopsis">
                  <Input.TextArea
                    onChange={e => handleLegacyFieldChange('seo_synopsis', e.target.value)}
                  />
                </Form.Item>
                <Form.Item label="SEO Keyword" name="seo_keyword">
                  <Input onChange={e => handleLegacyFieldChange('seo_keyword', e.target.value)} />
                </Form.Item> */}

                {/* Multilingual Title Input Field */}
              </Form>
            </div>
          </div>
          <Form.Item style={{ marginTop: 16, textAlign: 'center' }}>
            <Button
              type="primary"
              onClick={async () => {
                try {
                  // Prepare form data
                  const formValues = form.getFieldsValue();
                  console.log('📤 formValues:', formValues);

                  // Prepare i18n data in correct structure
                  const i18nStructuredData = {
                    ...formValues,
                    ...formData,
                    // Convert i18n data to correct structure
                    i18n: {
                      title: i18nData.title,
                      synopsis: i18nData.synopsis,
                      seo_title: i18nData.seo_title,
                      seo_synopsis: i18nData.seo_synopsis,
                      seo_keyword: i18nData.seo_keyword,
                    },
                    genres: genreList.map(genre => genre.id),
                    tags: tagList.map(tag => tag.id),
                  };

                  // Sync i18n data with legacy data
                  const finalFormData = I18nService.processMixedFormatData(i18nStructuredData);

                  // Create API request data (including i18n fields)
                  const apiRequest = I18nService.createApiRequest(finalFormData);

                  // Final update data (including both legacy + i18n fields + enhanced_cast)
                  const updateData = {
                    ...formValues,
                    ...apiRequest,
                    genres: genreList.map(genre => genre.id),
                    tags: tagList.map(tag => tag.id),
                    enhanced_cast: enhancedCast,
                  };

                  console.log('📤 API Request Data:', updateData); // For debugging

                  // Pass update data including i18n data directly
                  await libraryService.updateLibrary(id!, form, updateData);
                  message.success('Library updated successfully');
                  window.location.reload();
                } catch (error: any) {
                  // Form validation error handling
                  if (error?.errorFields && Array.isArray(error.errorFields)) {
                    const fieldErrors = error.errorFields.map((field: any) => {
                      const fieldName = Array.isArray(field.name)
                        ? field.name.join('.')
                        : field.name;
                      const errorMessage = Array.isArray(field.errors)
                        ? field.errors.join(', ')
                        : field.errors;
                      return `${fieldName}: ${errorMessage}`;
                    });

                    Modal.error({
                      title: 'Error',
                      content: (
                        <div>
                          <div style={{ marginBottom: 8, fontWeight: 'bold' }}>
                            Please check required fields:
                          </div>
                          {fieldErrors.map((error: string, index: number) => (
                            <div key={index} style={{ marginLeft: 8 }}>
                              • {error}
                            </div>
                          ))}
                        </div>
                      ),
                      okText: 'Confirm',
                    });
                    return;
                  }

                  // API error handling
                  if (axios.isAxiosError(error) && error.response?.status === 400) {
                    const errorData = error.response.data;
                    if (errorData && typeof errorData === 'object') {
                      const errorMessages = Object.entries(errorData).map(([field, messages]) => {
                        const fieldDisplayName = field;
                        const messageText = Array.isArray(messages)
                          ? messages.join(', ')
                          : messages;
                        return `${fieldDisplayName}: ${messageText}`;
                      });

                      Modal.error({
                        title: 'Error',
                        content: (
                          <div>
                            <div style={{ marginBottom: 8, fontWeight: 'bold' }}>
                              Update failed:
                            </div>
                            {errorMessages.map((msg: string, index: number) => (
                              <div key={index} style={{ marginLeft: 8 }}>
                                • {msg}
                              </div>
                            ))}
                          </div>
                        ),
                        okText: 'Confirm',
                      });
                    } else {
                      Modal.error({
                        title: 'Error',
                        content: 'Failed to update: ' + JSON.stringify(errorData),
                        okText: 'Confirm',
                      });
                    }
                  } else {
                    Modal.error({
                      title: 'Error',
                      content:
                        'An error occurred during update: ' +
                        (error.message || JSON.stringify(error)),
                      okText: 'Confirm',
                    });
                  }
                }
              }}
            >
              Save
            </Button>
          </Form.Item>
        </div>
      )}
    </>
  );
};

export default memo(LibraryDetail);
